// 4D Tetris - Main Entry Point

import { Game4D, GameEvents } from './game/Game4D';
import { Renderer } from './renderer/Renderer';
import { gameStorage, HighScore } from './storage/GameStorage';

class App {
  private game: Game4D;
  private renderer: Renderer;
  private lastTime: number = 0;
  private isRunning: boolean = true;

  // UI Elements
  private scoreEl: HTMLElement;
  private levelEl: HTMLElement;
  private dimensionEl: HTMLElement;
  private gameOverEl: HTMLElement;
  private finalScoreEl: HTMLElement;
  private restartBtn: HTMLElement;
  private pitIndicators: NodeListOf<Element>;

  constructor() {
    // Get UI elements
    this.scoreEl = document.getElementById('score')!;
    this.levelEl = document.getElementById('level')!;
    this.dimensionEl = document.getElementById('dimension')!;
    this.gameOverEl = document.getElementById('game-over')!;
    this.finalScoreEl = document.getElementById('final-score')!;
    this.restartBtn = document.getElementById('restart-btn')!;
    this.pitIndicators = document.querySelectorAll('.pit-indicator');

    // Game events
    const events: GameEvents = {
      onScoreChange: (score) => this.updateScore(score),
      onLevelChange: (level) => this.updateLevel(level),
      onLinesCleared: (lines) => this.onLinesCleared(lines),
      onGameOver: () => this.showGameOver(),
      onPieceSpawn: () => this.onPieceSpawn(),
      onPieceLock: () => this.onPieceLock(),
      onDimensionChange: (w) => this.updateDimension(w),
    };

    // Initialize game
    this.game = new Game4D(events);

    // Initialize renderer
    const container = document.getElementById('app')!;
    this.renderer = new Renderer(container, this.game.grid.config);

    // Set initial view dimension
    this.renderer.setCurrentW(this.game.currentW);
    this.updateDimension(this.game.currentW);

    // Spawn the first piece now that everything is initialized
    this.game.spawnPiece();
    this.updateRenderer();

    // Setup controls
    this.setupControls();

    // Setup restart button
    this.restartBtn.addEventListener('click', () => this.restart());

    // Load high scores on start
    this.loadHighScores();

    // Start game loop
    this.animate(0);
  }

  private setupControls(): void {
    document.addEventListener('keydown', (e) => {
      // Save/Load controls work even when paused
      if (e.key === 'F5') {
        e.preventDefault();
        this.quickSave();
        return;
      }
      if (e.key === 'F9') {
        e.preventDefault();
        this.quickLoad();
        return;
      }

      if (this.game.state === 'gameover') return;

      switch (e.key) {
        // Movement
        case 'ArrowLeft':
          this.game.moveX(-1);
          break;
        case 'ArrowRight':
          this.game.moveX(1);
          break;
        case 'ArrowUp':
          this.game.moveZ(-1);
          break;
        case 'ArrowDown':
          this.game.moveZ(1);
          break;

        // 4th dimension movement
        case 'q':
        case 'Q':
          this.game.moveW(-1);
          break;
        case 'e':
        case 'E':
          this.game.moveW(1);
          break;

        // Rotations
        case 'a':
        case 'A':
          this.game.rotate('xy');
          break;
        case 'd':
        case 'D':
          this.game.rotate('xy');
          this.game.rotate('xy');
          this.game.rotate('xy');
          break;
        case 'w':
        case 'W':
          this.game.rotate('xz');
          break;
        case 's':
        case 'S':
          this.game.rotate('xz');
          this.game.rotate('xz');
          this.game.rotate('xz');
          break;
        case 'z':
        case 'Z':
          this.game.rotate('xw');
          break;
        case 'x':
        case 'X':
          this.game.rotate('xw');
          this.game.rotate('xw');
          this.game.rotate('xw');
          break;

        // Drop
        case ' ':
          this.game.hardDrop();
          break;

        // View dimension switching (number keys)
        case '1':
          this.game.setViewDimension(0);
          this.renderer.setCurrentW(0);
          break;
        case '2':
          this.game.setViewDimension(1);
          this.renderer.setCurrentW(1);
          break;
        case '3':
          this.game.setViewDimension(2);
          this.renderer.setCurrentW(2);
          break;

        // Pause
        case 'p':
        case 'P':
          this.game.togglePause();
          break;
      }

      // Update the renderer with new block positions
      this.updateRenderer();
    });

    // Pit indicator clicks
    this.pitIndicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => {
        this.game.setViewDimension(index);
        this.renderer.setCurrentW(index);
        this.updateRenderer();
      });
    });
  }

  private updateRenderer(): void {
    const { gridBlocks, activeBlocks, ghostBlocks } = this.game.getVisibleBlocks();
    this.renderer.updateBlocks(gridBlocks, activeBlocks, ghostBlocks);
  }

  private updateScore(score: number): void {
    this.scoreEl.textContent = `Score: ${score.toLocaleString()}`;
  }

  private updateLevel(level: number): void {
    this.levelEl.textContent = `Level: ${level}`;
  }

  private updateDimension(w: number): void {
    this.dimensionEl.textContent = `Dimension W: ${w + 1}/${this.game.grid.config.sizeW}`;

    // Update pit indicators
    this.pitIndicators.forEach((indicator, index) => {
      indicator.classList.toggle('active', index === w);
    });
  }

  private onLinesCleared(lines: number): void {
    // Create particle effects for cleared lines
    for (let i = 0; i < lines; i++) {
      this.renderer.createLinesClearEffect(this.game.grid.config.sizeY - 1 - i);
    }
  }

  private onPieceSpawn(): void {
    this.updateRenderer();
  }

  private onPieceLock(): void {
    this.updateRenderer();
  }

  private async showGameOver(): Promise<void> {
    this.gameOverEl.style.display = 'block';
    this.finalScoreEl.textContent = this.game.score.toLocaleString();

    // Check if this is a high score
    const isHigh = await gameStorage.isHighScore(this.game.score);
    if (isHigh && this.game.score > 0) {
      const name = prompt('New high score! Enter your name:', 'Player') || 'Player';
      await this.saveHighScore(name);
    }

    await this.loadHighScores();
  }

  private async saveHighScore(playerName: string): Promise<void> {
    const highScore: Omit<HighScore, 'id'> = {
      score: this.game.score,
      level: this.game.level,
      linesCleared: this.game.linesCleared,
      date: new Date().toISOString(),
      playerName,
    };

    await gameStorage.addHighScore(highScore);
  }

  private async loadHighScores(): Promise<void> {
    const scores = await gameStorage.getTopScores(5);
    const highScoresList = document.getElementById('high-scores-list');

    if (highScoresList) {
      if (scores.length === 0) {
        highScoresList.innerHTML = '<li>No scores yet</li>';
      } else {
        highScoresList.innerHTML = scores
          .map(
            (s, i) =>
              `<li>${i + 1}. ${s.playerName} - ${s.score.toLocaleString()} (Lvl ${s.level})</li>`
          )
          .join('');
      }
    }
  }

  private async quickSave(): Promise<void> {
    if (this.game.state === 'gameover') return;

    await gameStorage.quickSave({
      gridState: this.game.grid.serialize(),
      score: this.game.score,
      level: this.game.level,
      linesCleared: this.game.linesCleared,
      currentW: this.game.currentW,
    });

    this.showNotification('Game saved!');
  }

  private async quickLoad(): Promise<void> {
    const save = await gameStorage.quickLoad();
    if (!save) {
      this.showNotification('No save found');
      return;
    }

    // Restore game state
    this.game.grid.loadState(save.gridState);
    this.game.score = save.score;
    this.game.level = save.level;
    this.game.linesCleared = save.linesCleared;
    this.game.currentW = save.currentW;
    this.game.state = 'playing';

    // Update UI
    this.updateScore(save.score);
    this.updateLevel(save.level);
    this.updateDimension(save.currentW);
    this.renderer.setCurrentW(save.currentW);
    this.gameOverEl.style.display = 'none';

    // Spawn a new piece
    this.game.spawnPiece();
    this.updateRenderer();

    this.showNotification('Game loaded!');
  }

  private showNotification(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'notification';
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('fade-out');
      setTimeout(() => notification.remove(), 500);
    }, 1500);
  }

  private restart(): void {
    this.gameOverEl.style.display = 'none';
    this.game.restart();
    this.renderer.setCurrentW(this.game.currentW);
    this.updateRenderer();
  }

  private animate(currentTime: number): void {
    if (!this.isRunning) return;

    requestAnimationFrame((t) => this.animate(t));

    const deltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    // Update game
    this.game.update(currentTime);

    // Update renderer
    this.updateRenderer();
    this.renderer.animate(deltaTime);
    this.renderer.render();
  }

  dispose(): void {
    this.isRunning = false;
    this.renderer.dispose();
  }
}

// Start the app
new App();
